import { Alert, Divider, Image, Spin, Tag } from "antd";
import {
  AlertTriangle,
  BarChart3,
  Building2,
  Calendar,
  Clock,
  CreditCard,
  FileText,
  Hash,
  Image as ImageIcon,
  Info,
  Landmark,
  Link2,
  MapPin,
  Maximize2,
  Monitor,
  Paperclip,
  Printer,
  Receipt,
  Shield,
  ShieldCheck,
  Store,
  TagIcon,
  User,
  Users,
} from "lucide-react";
import { useEffect, useMemo } from "react";

import type { ShopDetailVO } from "@/api/stores/type";
import { pickFirstOssUrl } from "@/api/stores/type";
import { DetailRow, SectionTitle } from "@/components/DetailBlocks";
import { useBrandSelect } from "@/components/SelectBrand/hooks/useBrandSelect";
import { useOssSignedUrl } from "@/hooks/useOssSignedUrl";
import { cn } from "@/lib/cn";
import { formatDate } from "@/utils";

import { getStoreScaleColor, STORE_SCALE_THEME } from "../../constants";
import type { StoreScale } from "../../types";

/** Tab 内容公共上下文 */
export interface StoreDetailTabContext {
  shop: ShopDetailVO;
  /** 是否编辑模式（双击字段可编辑） */
  isEditMode?: boolean;
  /** 行内编辑保存回调 */
  onEditShop?: (field: string, value: string) => void;
  /** 打开归属上级为代理时的详情 */
  onOpenAgent?: (agentId: string) => void;
  /** 打开归属上级为门店时的详情 */
  onOpenStore?: (storeId: string) => void;
  onOpenEntity?: (entityId: string) => void;
  onOpenAccount?: (accountId: string) => void;
}

/** 门店详情头部名片 */
export const StoreHeaderCard = ({ shop }: { shop: ShopDetailVO }) => {
  // 按规模标签取主题色，缺省小型
  const scaleTheme =
    STORE_SCALE_THEME[(shop.authScaleLabel ?? "小型") as StoreScale] ?? STORE_SCALE_THEME["小型"];

  return (
    <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl border">
      <div
        className={cn(
          "w-14 h-14 rounded-xl flex items-center justify-center text-white shadow-sm shrink-0",
          scaleTheme.gradient,
        )}
      >
        <Store className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h3 className="font-bold text-lg truncate m-0">
            {shop.shopShortName || shop.shopName || "—"}
          </h3>
          {shop.authScaleLabel ? (
            <Tag
              bordered
              color={getStoreScaleColor(shop.authScaleLabel)}
              className="m-0 text-[10px]"
            >
              {shop.authScaleLabel}
            </Tag>
          ) : null}
          {shop.beyondAgentRange === 1 ? (
            <Tag bordered color="error" className="m-0 text-[10px]">
              <AlertTriangle className="w-3 h-3 inline mr-0.5" />
              超范围
            </Tag>
          ) : null}
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
          <span className="font-mono">{shop.shopCode}</span>
          {shop.shopShortName && shop.shopName && shop.shopShortName !== shop.shopName ? (
            <span className="truncate">{shop.shopName}</span>
          ) : null}
        </div>
      </div>
    </div>
  );
};

/** 基本信息 Tab：门店信息 + 地址 + 经营数据 + 备注 */
export const StoreBasicTab = ({
  shop,
  isEditMode,
  onEditShop,
  onOpenAgent,
  onOpenStore,
}: StoreDetailTabContext) => {
  const { getBrandById, fetchBrandList } = useBrandSelect({ onlyEnabled: false });

  /** 打开 Tab 时预拉品牌列表，用于 ID 转名称 */
  useEffect(() => {
    void fetchBrandList();
  }, [fetchBrandList]);

  // 品牌展示：优先 brandTagName，其次 brandTags ID 转名称
  const brandLabel = useMemo(() => {
    if (shop.brandTagName) return shop.brandTagName;
    if (shop.brandTagId) {
      return getBrandById(String(shop.brandTagId))?.brandName ?? shop.brandTagId;
    }
    if (!shop.brandTags?.length) return "—";
    return shop.brandTags.map((id) => getBrandById(id)?.brandName ?? id).join("、");
  }, [getBrandById, shop.brandTagId, shop.brandTagName, shop.brandTags]);

  // 按归属上级类型打开对应详情：门店 / 代理；CHAIN 暂无详情栈
  const openParentOrg = () => () => {
    if (shop.parentOrgId && shop.parentOrgType === "STORE") {
      onOpenStore?.(shop.parentOrgId!);
    } else if (shop.parentOrgId && shop.parentOrgType === "AGENT") {
      onOpenAgent?.(shop.parentOrgId!);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <SectionTitle icon={Store} title="门店信息" />
        <div className="grid grid-cols-2 gap-x-6 gap-y-1">
          <DetailRow
            icon={Store}
            label="门店名称"
            value={shop.shopName}
            isEdit={isEditMode}
            onEditChange={(value) => onEditShop?.("shopName", value)}
          />
          <DetailRow
            icon={Shield}
            label="门店状态"
            type="dict"
            dictCode="inviteStatus"
            value={shop.status}
          />
          <DetailRow
            icon={Link2}
            label="归属上级"
            value={shop.agentName || "—"}
            onClick={openParentOrg()}
          />
          <DetailRow icon={TagIcon} label="品牌标签" value={brandLabel} />
          <DetailRow
            icon={Building2}
            label="门店类型"
            type="dict"
            dictCode="shopType"
            value={shop.shopType}
            isEdit={isEditMode}
            onEditChange={(value) => onEditShop?.("shopType", value)}
          />
          <DetailRow
            icon={Calendar}
            label="创建日期"
            value={shop.createAt ? formatDate(shop.createAt, "YYYY-MM-DD") : "—"}
          />
        </div>
      </div>
      <Divider className="my-2" />
      <div>
        <SectionTitle icon={MapPin} title="地址信息" />
        <div className="grid grid-cols-2 gap-x-6 gap-y-1">
          <DetailRow icon={MapPin} label="省/市/区" value={shop.locationPath || "—"} />
          <DetailRow
            icon={MapPin}
            label="详细地址"
            value={shop.detailedAddress || "—"}
            isEdit={isEditMode}
            onEditChange={(value) => onEditShop?.("detailedAddress", value)}
          />
          <DetailRow
            icon={MapPin}
            label="区域标签"
            value={shop.regionTags?.join("、") || "—"}
            isEdit={isEditMode}
            onEditChange={(value) => onEditShop?.("regionTags", value)}
          />
          <DetailRow
            icon={Hash}
            label="区域编码"
            value={shop.regionCodes?.length ? shop.regionCodes[shop.regionCodes.length - 1] : "—"}
          />
        </div>
      </div>
      {shop.beyondAgentRange === 1 ? (
        <Alert
          type="warning"
          showIcon
          icon={<AlertTriangle className="w-4 h-4" />}
          message={`该门店位置不在归属代理「${shop.agentName ?? "—"}」的代理范围内`}
        />
      ) : null}
      <Divider className="my-2" />
      <div>
        <SectionTitle icon={BarChart3} title="经营数据" />
        <div className="grid grid-cols-2 gap-x-6 gap-y-1">
          <DetailRow
            icon={Monitor}
            label="门店规模"
            value={`${shop.authDeviceQuantity ?? 0}台${shop.authScaleLabel ? `（${shop.authScaleLabel}）` : ""}`}
          />
          <DetailRow
            icon={Maximize2}
            label="门店面积"
            value={shop.areaSqm != null ? `${shop.areaSqm}㎡` : "—"}
            isEdit={isEditMode}
            onEditChange={(value) => onEditShop?.("areaSqm", value)}
          />
          <DetailRow
            icon={Clock}
            label="营业时间"
            value={shop.businessHours || "—"}
            isEdit={isEditMode}
            onEditChange={(value) => onEditShop?.("businessHours", value)}
          />
          <DetailRow
            icon={Users}
            label="会员数量"
            value={(shop.memberCount ?? 0).toLocaleString()}
          />
          <DetailRow icon={Users} label="员工数量" value={`${shop.employeeCount ?? 0} 人`} />
        </div>
      </div>
      <Divider className="my-2" />
      <div>
        <SectionTitle icon={Info} title="备注" />
        {shop.remark || isEditMode ? (
          <DetailRow
            icon={Info}
            label="备注"
            value={shop.remark || "—"}
            isEdit={isEditMode}
            onEditChange={(value) => onEditShop?.("remark", value)}
          />
        ) : (
          <p className="text-sm text-slate-400 m-0">暂无备注</p>
        )}
      </div>
    </div>
  );
};

/** 证照图片字段：签名 URL 缩略图 + 点击预览 */
const StorePhotoField = ({ label, fileUrl }: { label: string; fileUrl?: string }) => {
  const { signedUrl, loading, error } = useOssSignedUrl({
    fileUrl,
    enabled: !!fileUrl,
  });

  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1.5">
        <ImageIcon className="w-3.5 h-3.5 shrink-0" />
        <span>{label}</span>
      </div>
      {!fileUrl ? (
        <div className="h-24 rounded-lg border border-dashed border-slate-200 bg-slate-50/60 flex items-center justify-center text-sm text-slate-400">
          未上传
        </div>
      ) : (
        <Spin spinning={loading}>
          {signedUrl ? (
            <Image
              src={signedUrl}
              alt={label}
              width="100%"
              height={96}
              className="rounded-lg object-cover border border-slate-200 bg-white"
              style={{ objectFit: "cover" }}
            />
          ) : (
            !loading && (
              <div className="h-24 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center text-sm text-slate-400">
                {error ? "预览加载失败" : "—"}
              </div>
            )
          )}
        </Spin>
      )}
    </div>
  );
};

/** 证照管理 Tab */
export const StoreLicenseTab = ({ shop }: StoreDetailTabContext) => (
  <div className="space-y-4">
    <div>
      <SectionTitle icon={ImageIcon} title="门店照片" />
      <p className="text-xs text-slate-400 mb-2 -mt-2">需准备门店LOGO图、门头照、营业场所照片</p>
      <div className="grid grid-cols-2 gap-3">
        <StorePhotoField label="门店LOGO" fileUrl={pickFirstOssUrl(shop.shopLogo)} />
        <StorePhotoField label="门头照" fileUrl={pickFirstOssUrl(shop.doorPhotoUrl)} />
        <StorePhotoField label="营业场所照片" fileUrl={pickFirstOssUrl(shop.premisePhotoUrl)} />
      </div>
    </div>
    <Divider className="my-2" />
    <div>
      <SectionTitle icon={ImageIcon} title="网吧平面图" />
      <p className="text-xs text-slate-400 mb-2 -mt-2">如不开通订座功能可以不提供</p>
      <div className="grid grid-cols-2 gap-3">
        <StorePhotoField label="平面图" fileUrl={pickFirstOssUrl(shop.floorPlanUrl)} />
      </div>
    </div>
  </div>
);

/** 银行与开票 Tab */
export const StoreBankTab = ({ shop, isEditMode, onEditShop }: StoreDetailTabContext) => (
  <div className="space-y-4">
    <div>
      <SectionTitle icon={CreditCard} title="银行开户信息" />
      <div className="grid grid-cols-2 gap-x-6 gap-y-1">
        <DetailRow
          icon={User}
          label="收款名称"
          value={shop.accountName || "—"}
          isEdit={isEditMode}
          onEditChange={(value) => onEditShop?.("accountName", value)}
        />
        <DetailRow
          icon={CreditCard}
          label="收款卡号"
          value={shop.accountNo || "—"}
          isEdit={isEditMode}
          onEditChange={(value) => onEditShop?.("accountNo", value)}
        />
        <DetailRow
          icon={Landmark}
          label="银行名称"
          value={shop.bankName || "—"}
          isEdit={isEditMode}
          onEditChange={(value) => onEditShop?.("bankName", value)}
        />
      </div>
    </div>
    <Divider className="my-2" />
    <div>
      <SectionTitle icon={Receipt} title="开票信息" />
      <div className="grid grid-cols-2 gap-x-6 gap-y-1">
        <DetailRow
          icon={FileText}
          label="发票抬头"
          value={shop.invoiceTitle || "—"}
          isEdit={isEditMode}
          onEditChange={(value) => onEditShop?.("invoiceTitle", value)}
        />
        <DetailRow
          icon={Hash}
          label="纳税人识别号"
          value={shop.taxId || "—"}
          isEdit={isEditMode}
          onEditChange={(value) => onEditShop?.("taxId", value)}
        />
      </div>
    </div>
  </div>
);

/** 合同与其他 Tab */
export const StoreContractTab = ({ shop }: StoreDetailTabContext) => (
  <div className="space-y-4">
    <div>
      <SectionTitle icon={FileText} title="合同信息" />
      <div className="grid grid-cols-2 gap-x-6 gap-y-1">
        <DetailRow icon={FileText} label="合同编号" value={shop.contractNo || "—"} />
        <DetailRow
          icon={Paperclip}
          label="合同附件"
          value={shop.contractFileUrl ? "已上传" : "未上传"}
        />
      </div>
    </div>
    <Divider className="my-2" />
    <div>
      <SectionTitle icon={Monitor} title="授权信息" />
      <div className="grid grid-cols-2 gap-x-6 gap-y-1">
        <DetailRow
          icon={ShieldCheck}
          label="授权状态"
          value={
            shop.authStatusLabel ? (
              <Tag bordered color="success" className="m-0 text-[10px]">
                {shop.authStatusLabel}
              </Tag>
            ) : (
              shop.authStatus || "—"
            )
          }
        />
        <DetailRow
          icon={Calendar}
          label="授权日期"
          value={
            shop.authStartAt && shop.authEndAt
              ? `${shop.authStartAt} ~ ${shop.authEndAt}`
              : shop.authStartAt || shop.authEndAt || "—"
          }
        />
        <DetailRow
          icon={Monitor}
          label="授权终端数"
          value={`${shop.authDeviceQuantity ?? 0}台${shop.authScaleLabel ? `（${shop.authScaleLabel}）` : ""}`}
        />
        <DetailRow
          icon={CreditCard}
          label="终端单价"
          value={
            shop.deviceUnitPrice != null ? `¥${shop.deviceUnitPrice.toLocaleString()}/台/年` : "—"
          }
        />
        <DetailRow
          icon={CreditCard}
          label="授权费用"
          value={shop.authFee != null ? `¥${shop.authFee.toLocaleString()}/年` : "—"}
        />
      </div>
    </div>
    <Divider className="my-2" />
    <div>
      <SectionTitle icon={Printer} title="标签打印设备" />
      <p className="text-xs text-slate-400 mb-2 -mt-2">目前仅支持佳博 TD-2270</p>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1">
        <DetailRow icon={Printer} label="设备型号" value={shop.printerModel || "—"} />
        <DetailRow icon={Printer} label="设备编号" value={shop.printerDeviceNo || "—"} />
      </div>
    </div>
  </div>
);

/** 关联信息 Tab */
export const StoreInfoTab = ({
  shop,
  onOpenAgent,
  onOpenEntity,
  onOpenAccount,
}: StoreDetailTabContext) => (
  <div className="space-y-4">
    <div>
      <SectionTitle icon={Users} title="归属代理" />
      {shop.agentName ? (
        <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
          <div className="w-10 h-10 rounded-md bg-orange-500/10 flex items-center justify-center">
            <Users className="w-5 h-5 text-orange-500" />
          </div>
          <div className="flex-1 min-w-0">
            <button
              type="button"
              className="text-sm font-medium hover:underline text-orange-500 cursor-pointer truncate"
              onClick={shop.agentId && onOpenAgent ? () => onOpenAgent(shop.agentId!) : undefined}
            >
              {shop.agentName}
            </button>
            <p className="text-xs text-muted-foreground mt-0.5">代理名称</p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground m-0">未关联代理</p>
      )}
    </div>
    <Divider className="my-2" />
    <div>
      <SectionTitle icon={Building2} title="关联工商实体" />
      {shop.businessEntityName ? (
        <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
          <div className="w-10 h-10 rounded-md bg-orange-500/10 flex items-center justify-center">
            <Landmark className="w-5 h-5 text-orange-500" />
          </div>
          <div className="flex-1 min-w-0">
            <button
              type="button"
              className="text-sm font-medium hover:underline text-orange-500 cursor-pointer truncate"
              onClick={
                shop.businessEntityId && onOpenEntity
                  ? () => onOpenEntity(shop.businessEntityId!)
                  : undefined
              }
            >
              {shop.businessEntityName}
            </button>
            <p className="text-xs text-muted-foreground mt-0.5">工商实体名称</p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground m-0">未关联工商实体</p>
      )}
    </div>
    <Divider className="my-2" />
    <div>
      <SectionTitle icon={User} title="门店管理员" />
      {shop.managerUserName ? (
        <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
          <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center">
            <User className="w-5 h-5 text-orange-500" />
          </div>
          <div>
            <button
              type="button"
              className="text-sm font-medium hover:underline text-orange-500 cursor-pointer"
              onClick={shop.userId && onOpenAccount ? () => onOpenAccount(shop.userId!) : undefined}
            >
              {shop.managerUserName}
            </button>
            <p className="text-xs text-muted-foreground mt-0.5">平台归属自然人</p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground m-0">未关联门店管理员</p>
      )}
    </div>
  </div>
);
